import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {useQuery, keepPreviousData} from "@tanstack/react-query";
import {TournamentsApi, MatchLobbyResponse} from "@devour/client";
import getConfig from "@/utils/getConfig";

/**
 * Resolve a bracket match's lobby view: both teams' rosters with per-member
 * presence, team readiness, the game's lobby-join instructions, and — for
 * participants only — the generated lobby name/password. Backed by
 * `GET /tournaments/:tournamentId/matches/:matchId/lobby`
 * (`backend/src/routes/tournament/get_match_lobby.ts`).
 *
 * Polls while enabled: credentials appear at the READY transition and readiness
 * flips as teams ready up, so the match-detail view stays live without a socket.
 */
export function useGetMatchLobby(tournamentId?: string, matchId?: string, enabled = true) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: ["match-lobby", tournamentId, matchId, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<MatchLobbyResponse> =>
            new TournamentsApi(getConfig(fullToken)).getTournamentsTournamentIdMatchesMatchIdLobby({
                tournamentId: tournamentId!,
                matchId: matchId!,
            }),
        enabled: Boolean(tournamentId && matchId && enabled),
        staleTime: 15 * 1000, // 15s
        refetchInterval: 20 * 1000, // 20s
        placeholderData: keepPreviousData,
    });
}
